import { funEmoji } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";
import { ChevronUpIcon, PlusIcon } from "@heroicons/react/16/solid";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useAuth } from "@/auth";
import { Avatar } from "@/components/catalyst/Avatar";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownLabel,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import {
	Sidebar,
	SidebarBody,
	SidebarFooter,
	SidebarHeader,
	SidebarHeading,
	SidebarItem,
	SidebarLabel,
	SidebarSection,
	SidebarSpacer,
} from "@/components/catalyst/Sidebar";
import { Logo } from "@/components/Logo";
import { useNewAppDialog } from "@/hooks/use-new-app-dialog";
import { orpc } from "@/orpc/client";
import { cn } from "@/util/string";

interface NavigationItem {
	name: string;
	href: string;
	current: boolean;
	icon: React.ComponentType<{ className?: string }>;
}

interface AppSidebarProps {
	navigation: NavigationItem[];
	userNavigation: {
		name: string;
		href?: string;
		onClick?: () => void;
	}[];
}

export function AppSidebar({ navigation, userNavigation }: AppSidebarProps) {
	const { user } = useAuth();
	const { appId } = useParams({ strict: false });
	const { open: openNewAppDialog } = useNewAppDialog();

	const { data: apps } = useSuspenseQuery(orpc.apps.list.queryOptions());

	const avatar = useMemo(() => {
		if (user?.image) return user.image;
		const avatar = createAvatar(funEmoji, { seed: user.name });
		return avatar.toDataUri();
	}, [user]);

	return (
		<Sidebar>
			<SidebarHeader>
				<SidebarItem href="/">
					<Logo />
				</SidebarItem>
			</SidebarHeader>

			<SidebarBody>
				<SidebarSection>
					{navigation.map((item) => (
						<SidebarItem
							key={item.name}
							href={item.href}
							current={item.current}
						>
							<item.icon />
							<SidebarLabel>{item.name}</SidebarLabel>
						</SidebarItem>
					))}
				</SidebarSection>

				<SidebarSection>
					<SidebarHeading>Your apps</SidebarHeading>
					<SidebarItem
						onClick={openNewAppDialog}
						className="rounded-lg border-2 border-accent/70 cursor-pointer! hover:bg-accent transition-colors duration-200"
					>
						<PlusIcon />
						<SidebarLabel>New App</SidebarLabel>
					</SidebarItem>
					{apps.map((app) => (
						<SidebarItem
							key={app.id}
							href={`/apps/${app.id}`}
							current={appId === app.id}
						>
							<span
								className={cn(
									appId === app.id
										? "border-primary text-primary"
										: "border-base-content/20 text-base-content/60 group-hover:border-primary group-hover:text-primary",
									"flex size-6 shrink-0 items-center justify-center rounded-lg border bg-base-100 text-[0.625rem] font-medium dark:bg-base-200",
								)}
							>
								{app.name.charAt(0).toUpperCase()}
							</span>
							<SidebarLabel>{app.name}</SidebarLabel>
						</SidebarItem>
					))}
				</SidebarSection>
				<SidebarSpacer />
			</SidebarBody>

			<SidebarFooter>
				<SidebarSection>
					<SidebarItem href="/settings">
						<Cog6ToothIcon />
						<SidebarLabel>Settings</SidebarLabel>
					</SidebarItem>
				</SidebarSection>

				<SidebarSection>
					<Dropdown>
						<DropdownButton as={SidebarItem}>
							<span className="flex min-w-0 items-center gap-3">
								<Avatar
									src={avatar}
									className="size-10 bg-neutral border-neutral"
									square
									alt=""
								/>
								<span className="min-w-0">
									<span className="block truncate text-sm/5 font-medium text-base-content">
										{user.name}
									</span>
									<span className="block truncate text-xs/5 font-normal text-base-content/60">
										{user.email}
									</span>
								</span>
							</span>
							<ChevronUpIcon />
						</DropdownButton>
						<DropdownMenu className="min-w-64" anchor="top start">
							{userNavigation.map((item) => (
								<DropdownItem
									key={item.name}
									href={item.href}
									onClick={item.onClick}
								>
									<DropdownLabel>{item.name}</DropdownLabel>
								</DropdownItem>
							))}
						</DropdownMenu>
					</Dropdown>
				</SidebarSection>
			</SidebarFooter>
		</Sidebar>
	);
}
